<template>
  <!--
    Medicamentos da prescrição × Box
    Ordem de leitura de cada cartão (padrão F): status → nome → "o que vai para a Box" → ação.
    O que pede ação aparece primeiro (ordenação feita no backend).
  -->
  <section class="bxm" aria-labelledby="bxm-title">
    <!-- Estado vazio -->
    <div v-if="medicines.length == 0" class="bxm-empty" role="status">
      <i class="fas fa-pills bxm-empty__icon" aria-hidden="true"></i>
      <h3 class="bxm-empty__title">Prescrição sem medicamentos ativos</h3>
      <p class="bxm-empty__text">
        Não há medicamentos ativos e finalizados nesta prescrição para enviar à
        Box.
      </p>
    </div>

    <template v-else>
      <!-- Cabeçalho + resumo + ação em lote -->
      <header class="bxm-head">
        <div class="bxm-head__text">
          <h2 id="bxm-title" class="bxm-head__title">Medicamentos × Box</h2>
          <p class="bxm-head__subtitle">
            Escolha o fabricante, confira a prévia e envie. O status mostra se a
            Box está igual à prescrição.
          </p>
        </div>

        <span
          v-if="context && context.environment === 'HOMOLOGACAO'"
          class="bxm-env"
          role="note"
        >
          <i class="fas fa-flask mr-1" aria-hidden="true"></i>
          Ambiente de teste
        </span>
      </header>

      <!-- Filtros por status: contagem visível = noção do trabalho restante -->
      <div
        class="bxm-filters"
        role="group"
        aria-label="Filtrar medicamentos por situação"
      >
        <button
          v-for="f in filters"
          :key="f.code"
          type="button"
          class="bxm-filter"
          :class="{ 'bxm-filter--on': filter === f.code }"
          :aria-pressed="filter === f.code ? 'true' : 'false'"
          :disabled="f.count === 0 && f.code !== 'TODOS'"
          @click="filter = f.code"
        >
          {{ f.label }} <span class="bxm-filter__count">{{ f.count }}</span>
        </button>

        <div class="bxm-filters__spacer"></div>

        <ConfirmationDialog
          v-if="permission('write', 'box') && pendingWithChoice > 0"
          headerClass="modal-header"
          message-title="Enviar pendentes à Box"
          :message-body="`Serão enviados ou atualizados ${pendingWithChoice} medicamento(s) que já têm fabricante escolhido. Os demais ficam como estão.`"
          :open-text="`Enviar pendentes (${pendingWithChoice})`"
          open-class="btn-modern btn-modern-save"
          open-left-icon="fa-paper-plane"
          @yes="syncAll"
          :loading="busyAll"
          :disabled="busyAll || busyId !== null"
        />
      </div>

      <!-- Resultado da última ação (anunciado por leitor de tela) -->
      <div
        v-if="feedback"
        class="bxm-feedback"
        :class="`bxm-feedback--${feedback.tone}`"
        role="status"
        aria-live="polite"
      >
        <i
          :class="[
            'fas',
            feedback.tone === 'success'
              ? 'fa-check-circle'
              : 'fa-exclamation-triangle',
          ]"
          aria-hidden="true"
        ></i>
        <div>
          <strong>{{ feedback.title }}</strong>
          <ul v-if="feedback.items.length" class="bxm-feedback__list">
            <li v-for="(msg, i) in feedback.items" :key="i">{{ msg }}</li>
          </ul>
        </div>
        <button
          type="button"
          class="bxm-feedback__close"
          aria-label="Fechar aviso"
          @click="feedback = null"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Filtro sem itens: nunca deixar a área vazia sem explicação -->
      <div
        v-if="visibleMedicines.length === 0"
        class="bxm-empty bxm-empty--filter"
        role="status"
      >
        <p class="bxm-empty__text">
          Nenhum medicamento em "{{ currentFilterLabel }}".
          <button type="button" class="bxm-link" @click="filter = 'TODOS'">
            Ver todos ({{ medicines.length }})
          </button>
        </p>
      </div>

      <!-- Cartões -->
      <div v-else class="bxm-list">
        <article
          v-for="item in visibleMedicines"
          :key="item.id"
          class="bxm-card"
          :class="`bxm-card--${item.box.status.tone}`"
          :aria-labelledby="`bxm-name-${item.id}`"
        >
          <!-- 1. Status + identificação -->
          <header class="bxm-card__head">
            <span
              class="bxm-status"
              :class="`bxm-status--${item.box.status.tone}`"
            >
              <i :class="['fas', item.box.status.icon]" aria-hidden="true"></i>
              {{ item.box.status.label }}
            </span>

            <h3 :id="`bxm-name-${item.id}`" class="bxm-card__name">
              {{ item.medicine_name }}
            </h3>

            <p class="bxm-card__meta">
              <span>{{ item.medicine_active_ingredient }}</span>
              <span aria-hidden="true">·</span>
              <span>{{ item.route_of_administration_name }}</span>
              <span
                v-if="item.type_of_recipe_name"
                class="bxm-tag"
                :class="{ 'bxm-tag--danger': isControlled(item) }"
              >
                {{
                  isControlled(item)
                    ? "Controle especial"
                    : item.type_of_recipe_name
                }}
              </span>
            </p>
          </header>

          <div class="bxm-card__body">
            <!-- 2. Como está no SistemaClin -->
            <div class="bxm-col">
              <h4 class="bxm-col__title">Na prescrição</h4>

              <dl class="bxm-dl">
                <div>
                  <dt>Frequência</dt>
                  <dd>{{ frequencyText(item) }}</dd>
                </div>
                <div v-if="item.frequency !== 'EVENTUAL'">
                  <dt>Duração</dt>
                  <dd>{{ durationText(item) }}</dd>
                </div>
              </dl>

              <ul
                v-if="
                  item.frequency !== 'EVENTUAL' &&
                  item.prescription_medicine_time.length
                "
                class="bxm-times"
                aria-label="Horários e doses"
              >
                <li
                  v-for="t in item.prescription_medicine_time"
                  :key="t.id"
                  class="bxm-time"
                >
                  <span class="bxm-time__hour">{{
                    t.time ? String(t.time).substr(0, 5) : "--:--"
                  }}</span>
                  <span class="bxm-time__dose">{{ timeDose(t) }}</span>
                </li>
              </ul>

              <p v-if="item.instruction" class="bxm-instruction">
                <i class="fas fa-info-circle mr-1" aria-hidden="true"></i>
                {{ item.instruction }}
              </p>
            </div>

            <!-- 3. Como vai para a Box -->
            <div class="bxm-col">
              <h4 class="bxm-col__title">Na Box</h4>

              <div
                v-if="item.box.options.length === 0"
                class="bxm-note bxm-note--warning"
                role="note"
              >
                <i class="fas fa-question-circle mr-2" aria-hidden="true"></i>
                {{
                  item.box.options_message ||
                  "Nenhum medicamento correspondente no catálogo da Box."
                }}
              </div>

              <template v-else>
                <v-autocomplete
                  :id="`bxm-select-${item.id}`"
                  v-model="selection[item.id]"
                  :items="item.box.options"
                  item-value="id"
                  :item-text="optionText"
                  label="Medicamento e fabricante na Box"
                  outlined
                  dense
                  hide-details="auto"
                  :disabled="!permission('write', 'box') || busyId === item.id"
                  :hint="selectionHint(item)"
                  persistent-hint
                  no-data-text="Nenhum resultado. Digite parte do nome ou do fabricante."
                  class="bxm-autocomplete"
                  @change="onSelect(item)"
                >
                  <template v-slot:item="{ item: opt }">
                    <div class="bxm-opt">
                      <div class="bxm-opt__name">
                        {{ opt.medicamento }}
                        <span
                          v-if="opt.id === item.box.suggested_id"
                          class="bxm-opt__badge"
                          >Sugerido</span
                        >
                        <span
                          v-if="opt.previous"
                          class="bxm-opt__badge bxm-opt__badge--warn"
                          >Escolha anterior</span
                        >
                      </div>
                      <div class="bxm-opt__meta">
                        {{ opt.fabricante }} · {{ opt.forma }} ·
                        {{ opt.tipo_medicamento }}
                      </div>
                    </div>
                  </template>
                </v-autocomplete>

                <!-- Conferência imediata da dose com a forma escolhida -->
                <p
                  v-for="(msg, i) in doseIssues(item)"
                  :key="`dose-${i}`"
                  class="bxm-note bxm-note--danger"
                  role="alert"
                >
                  <i
                    class="fas fa-exclamation-triangle mr-2"
                    aria-hidden="true"
                  ></i
                  >{{ msg }}
                </p>
              </template>

              <!-- Prévia: exatamente o que será enviado -->
              <div class="bxm-preview" :aria-labelledby="`bxm-prev-${item.id}`">
                <p :id="`bxm-prev-${item.id}`" class="bxm-preview__label">
                  Será enviado assim
                </p>
                <p class="bxm-preview__summary">
                  {{ item.box.preview.summary || "—" }}
                </p>
                <p v-if="selectionChanged(item)" class="bxm-preview__stale">
                  Prévia calculada com o fabricante anterior. O envio confere de
                  novo.
                </p>

                <ul
                  v-if="item.box.preview.errors.length"
                  class="bxm-issues bxm-issues--danger"
                  role="alert"
                >
                  <li
                    v-for="(msg, i) in item.box.preview.errors"
                    :key="`e-${i}`"
                  >
                    {{ msg }}
                  </li>
                </ul>
                <ul
                  v-if="item.box.preview.warnings.length"
                  class="bxm-issues bxm-issues--warning"
                >
                  <li
                    v-for="(msg, i) in item.box.preview.warnings"
                    :key="`w-${i}`"
                  >
                    {{ msg }}
                  </li>
                </ul>
              </div>

              <p
                v-if="item.box.status.code === 'ERRO' && item.box.last_error"
                class="bxm-note bxm-note--danger"
                role="alert"
              >
                <strong>Resposta da Box:</strong> {{ item.box.last_error }}
              </p>
            </div>
          </div>

          <!-- 4. Ações (primária à direita, perto do fim da leitura) -->
          <footer class="bxm-card__foot">
            <button
              type="button"
              class="bxm-link"
              :aria-expanded="openHistory === item.id ? 'true' : 'false'"
              :aria-controls="`bxm-hist-${item.id}`"
              @click="toggleHistory(item)"
            >
              <i class="fas fa-history mr-1" aria-hidden="true"></i>
              Histórico
            </button>

            <span v-if="item.box.last_sent_at" class="bxm-card__sent">
              Último envio: {{ formatDate(item.box.last_sent_at) }}
            </span>

            <div class="bxm-card__actions">
              <ConfirmationDialog
                v-if="permission('write', 'box') && item.box.active_count > 0"
                headerClass="modal-header"
                message-title="Cancelar na Box"
                :message-body="`A Box deixa de produzir ${item.medicine_name} (${item.box.active_count} horário(s)). A prescrição no SistemaClin não muda.`"
                open-text="Cancelar na Box"
                open-class="btn-modern btn-modern-delete"
                open-left-icon="fa-ban"
                @yes="remove(item)"
                :loading="busyId === item.id"
                :disabled="busyId !== null"
              />

              <ConfirmationDialog
                v-if="permission('write', 'box') && primaryAction(item)"
                headerClass="modal-header"
                :message-title="primaryAction(item).title"
                :message-body="primaryAction(item).body"
                :open-text="primaryAction(item).label"
                open-class="btn-modern btn-modern-save"
                :open-left-icon="primaryAction(item).icon"
                @yes="sync(item)"
                :loading="busyId === item.id"
                :disabled="busyId !== null || !canSubmit(item)"
              />
              <span
                v-if="primaryAction(item) && !canSubmit(item)"
                class="bxm-card__why"
              >
                {{ blockReason(item) }}
              </span>
            </div>
          </footer>

          <!-- Histórico (auditoria) -->
          <div
            v-if="openHistory === item.id"
            :id="`bxm-hist-${item.id}`"
            class="bxm-history"
          >
            <p v-if="historyLoading" role="status">Carregando histórico…</p>
            <p v-else-if="!history.length">Nenhum envio registrado.</p>
            <table v-else class="bxm-history__table">
              <caption class="sr-only">
                Histórico de envios de
                {{
                  item.medicine_name
                }}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Quando</th>
                  <th scope="col">Ação</th>
                  <th scope="col">Quem</th>
                  <th scope="col">Resposta</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in history" :key="log.id">
                  <td>{{ formatDate(log.created_at) }}</td>
                  <td>{{ actionLabel(log.action) }}</td>
                  <td>{{ log.user_name || "Sistema" }}</td>
                  <td>{{ responseText(log) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Ferramenta de suporte: só com ?deleteInAnyWay=1 e permissão -->
          <div
            v-if="$route.query.deleteInAnyWay && permission('write', 'box')"
            class="bxm-support"
          >
            <button
              type="button"
              class="bxm-link bxm-link--danger"
              @click="remove(item, 'deleteInAnyWay')"
            >
              Suporte: limpar só o controle local (não chama a Box)
            </button>
          </div>
        </article>
      </div>
    </template>
  </section>
</template>

<script>
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import { formatDate } from "../../../lib/format";
import permission from "../../../lib/permission";
import toast from "../../../lib/ToastNotification";
import api from "../../../services/api";

const NEEDS_ACTION = [
  "ERRO",
  "DESATUALIZADO",
  "CONFERIR",
  "NAO_ENVIADO",
  "BLOQUEADO",
  "SEM_OPCAO",
];

export default {
  name: "BoxMedicine",
  components: { ConfirmationDialog },
  props: {
    box_id: [String, Number],
    resident_id: [String, Number],
    group_prescription_medicine_id: [String, Number],
    medicines: { type: Array, default: () => [] },
    context: { type: Object, default: null },
  },
  data() {
    return {
      selection: {}, // prescription_medicine_id -> boxifarma medicamento id
      filter: "ACAO",
      busyId: null,
      busyAll: false,
      feedback: null,
      openHistory: null,
      history: [],
      historyLoading: false,
    };
  },
  computed: {
    filters() {
      const count = (codes) =>
        this.medicines.filter((m) => codes.includes(m.box.status.code)).length;
      return [
        { code: "ACAO", label: "Pedem ação", count: count(NEEDS_ACTION) },
        { code: "EM_DIA", label: "Em dia", count: count(["EM_DIA"]) },
        { code: "TODOS", label: "Todos", count: this.medicines.length },
      ];
    },
    visibleMedicines() {
      if (this.filter === "TODOS") return this.medicines;
      if (this.filter === "EM_DIA")
        return this.medicines.filter((m) => m.box.status.code === "EM_DIA");
      return this.medicines.filter((m) =>
        NEEDS_ACTION.includes(m.box.status.code)
      );
    },
    currentFilterLabel() {
      const f = this.filters.find((x) => x.code === this.filter);
      return f ? f.label : "";
    },
    pendingWithChoice() {
      return this.medicines.filter(
        (m) =>
          m.box.choice &&
          ["DESATUALIZADO", "CONFERIR", "NAO_ENVIADO", "ERRO"].includes(
            m.box.status.code
          ) &&
          m.box.preview.can_send
      ).length;
    },
  },
  watch: {
    medicines: {
      immediate: true,
      handler(list) {
        // Pré-seleciona: escolha salva > sugestão (mesma concentração e forma)
        const selection = {};
        for (const m of list) {
          selection[m.id] = m.box.choice
            ? m.box.choice.id
            : m.box.suggested_id || null;
        }
        this.selection = selection;
        this.fitFilter();
      },
    },

    // Trocou de prescrição: começa limpo (aviso, histórico e filtro da anterior não valem)
    group_prescription_medicine_id() {
      this.feedback = null;
      this.openHistory = null;
      this.history = [];
      this.filter = "ACAO";
      this.fitFilter();
    },
  },
  methods: {
    permission,

    /**
     * Garante que o filtro ativo tenha itens.
     * Antes só "Pedem ação" era corrigido; se o usuário estava em "Em dia" e a nova
     * lista não tinha nenhum, a tela mostrava só os filtros, sem nenhum cartão.
     */
    fitFilter() {
      const list = this.medicines || [];
      const hasAction = list.some((m) =>
        NEEDS_ACTION.includes(m.box.status.code)
      );
      const hasOk = list.some((m) => m.box.status.code === "EM_DIA");

      if (this.filter === "ACAO" && !hasAction) this.filter = "TODOS";
      if (this.filter === "EM_DIA" && !hasOk)
        this.filter = hasAction ? "ACAO" : "TODOS";
    },

    formatDate(value) {
      return value ? formatDate(value) : "";
    },

    isControlled(item) {
      return /CONTROLE ESPECIAL/i.test(item.type_of_recipe_name || "");
    },

    optionText(opt) {
      return `${opt.medicamento} — ${opt.fabricante || "sem fabricante"} (${
        opt.forma || "forma não informada"
      })`;
    },

    selectedOption(item) {
      return (
        item.box.options.find((o) => o.id === this.selection[item.id]) || null
      );
    },

    selectionChanged(item) {
      const saved = item.box.choice
        ? item.box.choice.id
        : item.box.suggested_id;
      return !!this.selection[item.id] && this.selection[item.id] !== saved;
    },

    selectionHint(item) {
      const opt = this.selectedOption(item);
      if (!opt) return "Escolha o item que a farmácia vai separar.";
      const doses = opt.forma_dosagem_configurada
        ? `aceita doses ${opt.forma_dosagem_configurada}`
        : "aceita qualquer dose";
      const split =
        opt.forma_tipo_dose === "INTEIRO"
          ? "não pode ser partido"
          : "pode ser fracionado";
      return `${opt.forma}: ${split}, ${doses}.`;
    },

    onSelect() {
      this.feedback = null;
    },

    /** Mesma regra do backend (BoxScheduleMapper.checkDose), para aviso imediato. */
    doseIssues(item) {
      const opt = this.selectedOption(item);
      if (!opt) return [];
      const issues = new Set();
      const doses = item.box.preview.items
        .map((i) => Number(i.dose))
        .filter((d) => d > 0);

      for (const dose of doses) {
        if (opt.forma_tipo_dose === "INTEIRO" && !Number.isInteger(dose)) {
          issues.add(
            `${opt.forma} não pode ser partido, e há dose de ${dose}.`
          );
        }
        const rule = String(opt.forma_dosagem_configurada || "").trim();
        if (rule) {
          const ok = rule
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .some((token) => {
              const range = token.match(
                /^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/
              );
              return range
                ? dose >= Number(range[1]) && dose <= Number(range[2])
                : Number(token) === dose;
            });
          if (!ok)
            issues.add(
              `${opt.forma} aceita só as doses ${rule}; há dose de ${dose}.`
            );
        }
      }
      return [...issues];
    },

    canSubmit(item) {
      if (!this.selection[item.id]) return false;
      if (this.doseIssues(item).length) return false;
      // Erros de agenda (frequência sem equivalente, sem horário...) não mudam com o fabricante
      const agendaErrors = item.box.preview.errors.filter(
        (e) => !/forma|dose|fabricante|Escolha o medicamento/i.test(e)
      );
      return agendaErrors.length === 0;
    },

    blockReason(item) {
      if (!this.selection[item.id]) return "Escolha o fabricante.";
      if (this.doseIssues(item).length) return "Dose incompatível com a forma.";
      return "Corrija os itens em vermelho.";
    },

    /** Uma só ação primária por cartão, com texto que diz o efeito. */
    primaryAction(item) {
      const name = item.medicine_name;
      switch (item.box.status.code) {
        case "NAO_ENVIADO":
        case "BLOQUEADO":
          return {
            label: "Enviar à Box",
            icon: "fa-paper-plane",
            title: "Enviar à Box",
            body: `Enviar ${name} à Box? ${item.box.preview.summary || ""}`,
          };
        case "DESATUALIZADO":
        case "CONFERIR":
          return {
            label: "Atualizar na Box",
            icon: "fa-sync-alt",
            title: "Atualizar na Box",
            body: `A Box passa a produzir ${name} assim: ${
              item.box.preview.summary || ""
            }`,
          };
        case "ERRO":
          return {
            label: "Tentar de novo",
            icon: "fa-redo",
            title: "Reenviar",
            body: `Reenviar ${name} à Box?`,
          };
        case "EM_DIA":
          return this.selectionChanged(item)
            ? {
                label: "Trocar fabricante",
                icon: "fa-exchange-alt",
                title: "Trocar fabricante na Box",
                body: `A Box passa a usar o novo fabricante para ${name}.`,
              }
            : null;
        default:
          return null;
      }
    },

    showReport(data, okTitle) {
      if (!data.error) {
        toast(data.message || okTitle, "success");
        this.feedback = {
          tone: "success",
          title: data.message || okTitle,
          items: [],
        };
      } else {
        this.feedback = {
          tone: "danger",
          title:
            "A Box não aceitou tudo. Nada foi marcado como enviado sem confirmação.",
          items: data.errors || [],
        };
      }
    },

    async sync(item) {
      this.busyId = item.id;
      this.feedback = null;
      try {
        const { data } = await api.post("/rest-home/box/prescription/sync", {
          box_id: this.box_id,
          resident_id: this.resident_id,
          prescription_medicine_id: item.id,
          boxifarma_medicamento_id: this.selection[item.id],
        });
        this.showReport(data, "Enviado à Box");
        this.$emit("success");
      } catch (error) {
        this.feedback = {
          tone: "danger",
          title: "Falha de comunicação com o servidor.",
          items: [],
        };
      } finally {
        this.busyId = null;
      }
    },

    async syncAll() {
      this.busyAll = true;
      this.feedback = null;
      try {
        const { data } = await api.post(
          "/rest-home/box/prescription/sync-all",
          {
            box_id: this.box_id,
            resident_id: this.resident_id,
            group_prescription_medicine_id: this.group_prescription_medicine_id,
          }
        );
        this.showReport(data, "Pendentes enviados");
        this.$emit("success");
      } catch (error) {
        this.feedback = {
          tone: "danger",
          title: "Falha de comunicação com o servidor.",
          items: [],
        };
      } finally {
        this.busyAll = false;
      }
    },

    async remove(item, deleteInAnyWay = null) {
      this.busyId = item.id;
      this.feedback = null;
      try {
        const { data } = await api.put("/rest-home/box/remove/prescription", {
          id: item.id,
          box_id: this.box_id,
          resident_id: this.resident_id,
          deleteInAnyWay,
        });
        this.showReport(data, "Cancelado na Box");
        this.$emit("success");
      } catch (error) {
        this.feedback = {
          tone: "danger",
          title: "Falha de comunicação com o servidor.",
          items: [],
        };
      } finally {
        this.busyId = null;
      }
    },

    async toggleHistory(item) {
      if (this.openHistory === item.id) {
        this.openHistory = null;
        return;
      }
      this.openHistory = item.id;
      this.historyLoading = true;
      this.history = [];
      try {
        const { data } = await api.get("/rest-home/box/prescription/history", {
          params: {
            box_id: this.box_id,
            resident_id: this.resident_id,
            prescription_medicine_id: item.id,
          },
        });
        this.history = data.logs || [];
      } catch (error) {
        this.history = [];
      } finally {
        this.historyLoading = false;
      }
    },

    actionLabel(action) {
      return (
        {
          CRIAR: "Enviado",
          ATUALIZAR: "Atualizado",
          INATIVAR: "Cancelado",
          EXPIRAR: "Encerrado (fim)",
          ERRO: "Erro",
        }[action] || action
      );
    },

    responseText(log) {
      let body = log.response_json;
      if (typeof body === "string") {
        try {
          body = JSON.parse(body);
        } catch (e) {
          return body;
        }
      }
      if (!body) return log.http_status ? `HTTP ${log.http_status}` : "—";
      return body.message || (body.success ? "OK" : `HTTP ${log.http_status}`);
    },

    frequencyText(item) {
      const freq = {
        DIARIO: "dia",
        SEMANAL: "semana",
        MENSAL: "mês",
        ANUAL: "ano",
        QUINZENAL: "15 dias",
      };
      if (item.frequency === "EVENTUAL") return "Se necessário";
      const every = Number(item.every) || 1;
      let text =
        every === 1
          ? `Todo(a) ${freq[item.frequency] || item.frequency}`
          : `A cada ${every} ${freq[item.frequency] || ""}`;
      if (item.frequency === "SEMANAL") {
        const days = [
          ["dom", "dom"],
          ["seg", "seg"],
          ["ter", "ter"],
          ["qua", "qua"],
          ["qui", "qui"],
          ["sex", "sex"],
          ["sab", "sáb"],
        ]
          .filter(([k]) => Number(item[k]))
          .map(([, label]) => label);
        if (days.length) text += ` (${days.join(", ")})`;
      }
      return text;
    },

    durationText(item) {
      if (item.duration === "ATE_A_DATA")
        return `Até ${formatDate(item.end_date, false)}`;
      if (item.duration === "DURANTE_UM_PERIODO")
        return `${item.duration_time} dia(s) a partir de ${formatDate(
          item.start_date,
          false
        )}`;
      return "Contínuo";
    },

    timeDose(t) {
      if (t.drops) return `${t.drops} gota(s)`;
      if (t.ui) return `${t.ui} UI`;
      return `${t.quantity} ${t.unit_of_measurement_value || ""}`.trim();
    },
  },
};
</script>

<style scoped>
/* Tokens locais: não alteram o CSS padrão do sistema */
.bxm {
  --bxm-text: #1e293b;
  --bxm-muted: #475569; /* 7.0:1 sobre branco */
  --bxm-border: #cbd5e1;
  --bxm-surface: #ffffff;
  --bxm-soft: #f8fafc;
  --bxm-primary: #0f65b0;
  --bxm-success: #047857; /* 5.5:1 */
  --bxm-success-bg: #ecfdf5;
  --bxm-warning: #92400e; /* 7.1:1 */
  --bxm-warning-bg: #fffbeb;
  --bxm-danger: #b91c1c; /* 6.5:1 */
  --bxm-danger-bg: #fef2f2;
  --bxm-radius: 12px;
  color: var(--bxm-text);
}

/* Vazio */
.bxm-empty {
  background: var(--bxm-surface);
  border-radius: var(--bxm-radius);
  padding: 56px 24px;
  text-align: center;
}
.bxm-empty__icon {
  font-size: 48px;
  color: #94a3b8;
  margin-bottom: 16px;
}
.bxm-empty__title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px;
}
.bxm-empty__text {
  color: var(--bxm-muted);
  margin: 0;
}
.bxm-empty--filter {
  padding: 24px;
  border: 1px dashed var(--bxm-border);
}

/* Cabeçalho */
.bxm-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.bxm-head__title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
}
.bxm-head__subtitle {
  color: var(--bxm-muted);
  font-size: 15px;
  margin: 0;
}
.bxm-env {
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ede9fe;
  color: #5b21b6;
  font-weight: 600;
  font-size: 13px;
}

/* Filtros */
.bxm-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.bxm-filters__spacer {
  flex: 1;
}
.bxm-filter {
  min-height: 40px; /* alvo de toque >= 40px (Fitts) */
  padding: 0 14px;
  border: 1px solid var(--bxm-border);
  border-radius: 999px;
  background: var(--bxm-surface);
  color: var(--bxm-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.bxm-filter--on {
  background: var(--bxm-primary);
  border-color: var(--bxm-primary);
  color: #fff;
}
.bxm-filter:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.bxm-filter__count {
  display: inline-block;
  min-width: 22px;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  font-weight: 700;
}
.bxm-filter--on .bxm-filter__count {
  background: rgba(255, 255, 255, 0.25);
}

/* Feedback */
.bxm-feedback {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 16px;
  border-radius: var(--bxm-radius);
  margin-bottom: 16px;
  font-size: 15px;
}
.bxm-feedback--success {
  background: var(--bxm-success-bg);
  color: var(--bxm-success);
  border: 1px solid #6ee7b7;
}
.bxm-feedback--danger {
  background: var(--bxm-danger-bg);
  color: var(--bxm-danger);
  border: 1px solid #fca5a5;
}
.bxm-feedback__list {
  margin: 6px 0 0;
  padding-left: 18px;
}
.bxm-feedback__close {
  margin-left: auto;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

/* Cartões */
.bxm-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.bxm-card {
  background: var(--bxm-surface);
  border: 1px solid var(--bxm-border);
  border-left-width: 6px; /* faixa de status: leitura periférica */
  border-radius: var(--bxm-radius);
  overflow: hidden;
}
.bxm-card--success {
  border-left-color: var(--bxm-success);
}
.bxm-card--warning {
  border-left-color: #d97706;
}
.bxm-card--danger {
  border-left-color: var(--bxm-danger);
}
.bxm-card--neutral {
  border-left-color: #94a3b8;
}

.bxm-card__head {
  padding: 16px 20px 8px;
}
.bxm-card__name {
  font-size: 19px;
  font-weight: 700;
  margin: 8px 0 4px;
  line-height: 1.3;
}
.bxm-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  color: var(--bxm-muted);
  font-size: 14px;
  margin: 0;
}

.bxm-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
}
.bxm-status--success {
  background: var(--bxm-success-bg);
  color: var(--bxm-success);
}
.bxm-status--warning {
  background: var(--bxm-warning-bg);
  color: var(--bxm-warning);
}
.bxm-status--danger {
  background: var(--bxm-danger-bg);
  color: var(--bxm-danger);
}
.bxm-status--neutral {
  background: #f1f5f9;
  color: var(--bxm-muted);
}

.bxm-tag {
  padding: 2px 8px;
  border-radius: 6px;
  background: #e0f2fe;
  color: #075985;
  font-size: 12px;
  font-weight: 600;
}
.bxm-tag--danger {
  background: var(--bxm-danger-bg);
  color: var(--bxm-danger);
}

.bxm-card__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 20px;
  padding: 8px 20px 16px;
}
.bxm-col__title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bxm-muted);
  margin: 0 0 10px;
}

.bxm-dl {
  margin: 0 0 10px;
}
.bxm-dl > div {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 15px;
}
.bxm-dl dt {
  color: var(--bxm-muted);
  font-weight: 500;
  min-width: 90px;
}
.bxm-dl dd {
  margin: 0;
  font-weight: 600;
}

.bxm-times {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}
.bxm-time {
  display: flex;
  flex-direction: column;
  min-width: 84px;
  padding: 6px 10px;
  border: 1px solid var(--bxm-border);
  border-radius: 8px;
  background: var(--bxm-soft);
}
.bxm-time__hour {
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.bxm-time__dose {
  font-size: 13px;
  color: var(--bxm-muted);
}

.bxm-instruction {
  margin: 10px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--bxm-warning-bg);
  color: var(--bxm-warning);
  font-size: 14px;
}

.bxm-opt {
  padding: 6px 0;
}
.bxm-opt__name {
  font-weight: 600;
}
.bxm-opt__meta {
  font-size: 13px;
  color: var(--bxm-muted);
}
.bxm-opt__badge {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 6px;
  background: var(--bxm-success-bg);
  color: var(--bxm-success);
  font-size: 11px;
  font-weight: 700;
}
.bxm-opt__badge--warn {
  background: var(--bxm-warning-bg);
  color: var(--bxm-warning);
}

.bxm-note {
  display: flex;
  align-items: flex-start;
  margin: 10px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 14px;
}
.bxm-note--warning {
  background: var(--bxm-warning-bg);
  color: var(--bxm-warning);
}
.bxm-note--danger {
  background: var(--bxm-danger-bg);
  color: var(--bxm-danger);
}

.bxm-preview {
  margin-top: 14px;
  padding: 12px;
  border: 1px dashed var(--bxm-border);
  border-radius: 8px;
  background: var(--bxm-soft);
}
.bxm-preview__label {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bxm-muted);
}
.bxm-preview__summary {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
}
.bxm-preview__stale {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--bxm-warning);
}
.bxm-issues {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 14px;
}
.bxm-issues--danger {
  color: var(--bxm-danger);
}
.bxm-issues--warning {
  color: var(--bxm-warning);
}

.bxm-card__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid #e2e8f0;
  background: var(--bxm-soft);
}
.bxm-card__sent {
  color: var(--bxm-muted);
  font-size: 13px;
}
.bxm-card__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.bxm-card__why {
  font-size: 13px;
  color: var(--bxm-danger);
}

.bxm-link {
  min-height: 36px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: var(--bxm-primary);
  font-weight: 600;
  cursor: pointer;
}
.bxm-link--danger {
  color: var(--bxm-danger);
}

.bxm-history {
  padding: 12px 20px 16px;
  border-top: 1px solid #e2e8f0;
  font-size: 14px;
}
.bxm-history__table {
  width: 100%;
  border-collapse: collapse;
}
.bxm-history__table th,
.bxm-history__table td {
  padding: 6px 8px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  vertical-align: top;
}
.bxm-history__table th {
  color: var(--bxm-muted);
  font-weight: 600;
}

.bxm-support {
  padding: 8px 20px 12px;
  border-top: 1px dashed var(--bxm-danger);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

/* Foco visível em tudo que é clicável */
.bxm button:focus-visible {
  outline: 3px solid #f59e0b;
  outline-offset: 2px;
}

@media (max-width: 900px) {
  .bxm-card__body {
    grid-template-columns: 1fr;
  }
  .bxm-head {
    flex-direction: column;
  }
  .bxm-card__actions {
    margin-left: 0;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .bxm * {
    transition: none !important;
    animation: none !important;
  }
}
</style>